import { action, observable, runInAction, makeObservable } from "mobx";
import api from "../../api";
import { GeneralDocument, IDocument, Invoice, OrderDetail, PurchaseOrder, Receipt, Requisition } from "./Document";

const ORDER_NUMBER_API_LENGTH = 10;

class DocumentsStore {
  openOrders: OrderDetail[] = [];
  completedOrders: OrderDetail[] = [];
  requisitions: Requisition[] = [];
  purchaseOrders: PurchaseOrder[] = [];
  receipts: Receipt[] = [];
  invoices: Invoice[] = [];
  generalDocument?: GeneralDocument | null = undefined;

  document?: IDocument | null = undefined;

  constructor() {
    console.warn(
      "Deprecation Warning: The legacy 'DocumentsStore' is in use. Please migrate to RTK Query as outlined in CONTRIBUTING.md."
    );
    makeObservable(this, {
      openOrders: observable,
      completedOrders: observable,
      requisitions: observable,
      purchaseOrders: observable,
      receipts: observable,
      invoices: observable,
      generalDocument: observable,
      document: observable,
      getOpenOrders: action.bound,
      getCompletedOrders: action.bound,
      getRequisitions: action.bound,
      getPurchasesOrders: action.bound,
      getReceipts: action.bound,
      getInvoices: action.bound,
      getDocument: action.bound,
      sendPurchaseOrderToManufacturer: action.bound,
      getDocumentAsPDF: action.bound,
      sendDocumentEmail: action.bound,
      createPurchaseOrder: action.bound,
      cancelPurchaseOrder: action.bound,
      cancelRequisition: action.bound,
      editExpectedDate: action.bound,
      editPartNumberQuantity: action.bound,
      editPartNumberUnitPrice: action.bound,
      insertManufacturerInvoice: action.bound,
      getGeneralDocument: action.bound
    });
  }

  async getOpenOrders(applicationId: string) {
    try {
      const openOrders = await api.documents.retrieveOpenOrders(applicationId);
      const mappedOpenOrders = openOrders.map(OrderDetail.convertFrom);

      runInAction(() => {
        this.openOrders = mappedOpenOrders;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.openOrders = [];
      });
    }
  }

  async getCompletedOrders(applicationId: string) {
    try {
      const completedOrders = await api.documents.retrieveCompletedOrders(applicationId);
      const mappedCompletedOrders = completedOrders.map(OrderDetail.convertFrom);

      runInAction(() => {
        this.completedOrders = mappedCompletedOrders;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.completedOrders = [];
      });
    }
  }

  public getResellerOrders(resellerId: string) {
    return this.openOrders.filter(o => o.resellerId === resellerId);
  }

  public getProductOrders(randmarSKU: string) {
    return this.openOrders.filter(o => o.randmarSKU === randmarSKU);
  }

  public getCategoryOrders(categoryCode: string) {
    return this.openOrders.filter(o => o.category === categoryCode);
  }

  async regenerateDraftPurchases(applicationId: string): Promise<boolean> {
    try {
      return await api.documents.regenerateDraftPurchases(applicationId);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getRequisitions(applicationId: string) {
    try {
      const requisitions = await api.documents.retrieveRequisitions(applicationId);
      const mappedRequisitions = requisitions.map(Requisition.convertFrom);

      runInAction(() => {
        this.requisitions = mappedRequisitions;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.requisitions = [];
      });
    }
  }

  async getPurchasesOrders(applicationId: string) {
    try {
      const purchaseOrders = await api.documents.retrievePurchaseOrders(applicationId);
      const mappedPurchaseOrders = purchaseOrders.map(PurchaseOrder.convertFrom);

      runInAction(() => {
        this.purchaseOrders = mappedPurchaseOrders;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.purchaseOrders = [];
      });
    }
  }

  async getReceipts(applicationId: string) {
    try {
      const receipts = await api.documents.retrieveReceipts(applicationId);
      const mappedReceipts = receipts.map(Receipt.convertFrom);

      runInAction(() => {
        this.receipts = mappedReceipts;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.receipts = [];
      });
    }
  }

  async getInvoices(applicationId: string) {
    try {
      const invoices = await api.documents.retrieveInvoices(applicationId);
      const mappedInvoices = invoices.map(Invoice.convertFrom);

      runInAction(() => {
        this.invoices = mappedInvoices;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.invoices = [];
      });
    }
  }

  async getDocument(applicationId: string, documentNumber: string) {
    try {
      const document = await api.documents.retrieveDocument(applicationId, documentNumber);
      runInAction(() => {
        this.document = document;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.document = null;
      });
    }
  }

  async sendPurchaseOrderToManufacturer(applicationId: string, documentNumber: string): Promise<boolean> {
    try {
      return await api.documents.sendPurchaseOrderToManufacturer(applicationId, documentNumber);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getDocumentAsPDF(applicationId: string, documentNumber: string): Promise<string> {
    try {
      return await api.documents.retrieveDocumentAsPDF(applicationId, documentNumber);
    } catch (e) {
      console.error(e);
      return "";
    }
  }

  async sendDocumentEmail(
    applicationId: string,
    documentNumber: string,
    email: string,
    comment: string
  ): Promise<boolean> {
    try {
      return await api.documents.sendDocumentEmail(applicationId, documentNumber, email, comment);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async createPurchaseOrder(applicationId: string, requisitions: string[], sendToManufacturer: boolean): Promise<string> {
    try {
      return await api.documents.createPurchaseOrder(applicationId, requisitions, sendToManufacturer);
    } catch (e) {
      console.error(e);
      return "";
    }
  }

  async cancelPurchaseOrder(applicationId: string, purchaseOrderNumber: string): Promise<boolean> {
    try {
      return await api.documents.cancelPurchaseOrder(applicationId, purchaseOrderNumber);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async cancelRequisition(applicationId: string, requisitionNumber: string): Promise<boolean> {
    try {
      return await api.documents.cancelRequisition(applicationId, requisitionNumber);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async editExpectedDate(applicationId: string, purchaseOrderNumber: string, newDate: string): Promise<boolean> {
    try {
      return await api.documents.editExpectedDate(applicationId, purchaseOrderNumber, newDate);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async editPartNumberQuantity(
    applicationId: string,
    documentNumber: string,
    randmarSKU: string,
    newQuantity: number
  ): Promise<boolean> {
    try {
      if (documentNumber.startsWith("RQN"))
        return await api.documents.editPartNumberRequisition(applicationId, documentNumber, randmarSKU, newQuantity);
      else if (documentNumber.startsWith("PO"))
        return await api.documents.editPartNumberPurchaseOrder(applicationId, documentNumber, randmarSKU, newQuantity);

      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async editPartNumberUnitPrice(
    applicationId: string,
    documentNumber: string,
    randmarSKU: string,
    newUnitPrice: number,
    editStandardCost: boolean
  ): Promise<boolean> {
    try {
      if (documentNumber.startsWith("PO")) return await api.documents.editPartNumberUnitPrice(applicationId, documentNumber, randmarSKU, newUnitPrice, editStandardCost);
      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async insertManufacturerInvoice(
    applicationId: string,
    receiptNumber: string,
    invoiceNumber: string,
    invoiceDate: string,
    invoiceTotal: number
  ): Promise<boolean> {
    try {
      return await api.documents.vendorInvoice(applicationId, receiptNumber, invoiceNumber, invoiceDate, invoiceTotal);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getGeneralDocument(applicationId: string, orderNumber: string) {
    try {
      const generalDocument = await api.documents.retrieveGeneralDocument(applicationId, orderNumber.substring(0, ORDER_NUMBER_API_LENGTH));
      const mappedGeneralDocument = GeneralDocument.convertFrom(generalDocument);

      runInAction(() => { this.generalDocument = mappedGeneralDocument; });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.generalDocument = null;
      });
    }
  }
}
export default DocumentsStore;
